import { useEffect, useRef, useState } from "react";
import { Link } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { CheckIcon, CloseIcon, MenuButton, MoreIcon, PlusIcon, SendIcon, SparkIcon, TrashIcon, useEscapeLayer, useToast } from "@signet/ui";
import { ago, api, Markdown, money, stream, useMe } from "@signet/web-kit";

/* ──────────────────────────────────────────────────────────────────────────
   The assistant, beside whatever page is open.

   Not a modal: the page stays usable, because the usual way to use it is to
   ask about what is on screen. Answers stream in. Tool calls show as quiet
   chips so it is clear what was looked at. Anything that would change data
   arrives as a proposal card with Approve and Reject, and nothing changes
   until one is pressed.
   ────────────────────────────────────────────────────────────────────────── */

type Proposal = { id: string; summary: string; status: "pending" | "approved" | "rejected" | "failed"; error: string | null; result: unknown };
type ToolChip = { id: string; name: string; summary: string; state: "start" | "done" | "error" };
type Turn = { role: "user" | "assistant"; text: string; tools: ToolChip[]; proposals: Proposal[]; error?: string; costUsd?: number };
type Stored = { messages: { role: "user" | "assistant"; text: string; tools: { name: string; summary: string }[] }[]; proposals: Proposal[] };

const LAST_KEY = "signet.assistant.conversation";

function readLast(): string | null {
  try {
    return localStorage.getItem(LAST_KEY);
  } catch {
    return null;
  }
}

function writeLast(id: string | null) {
  try {
    if (id) localStorage.setItem(LAST_KEY, id);
    else localStorage.removeItem(LAST_KEY);
  } catch {
    /* private window: the conversation just is not remembered */
  }
}

export function AssistantPanel({ open, onClose }: { open: boolean; onClose(): void }) {
  const { me } = useMe();
  const toast = useToast();
  const queryClient = useQueryClient();
  const [conversationId, setConversationId] = useState<string | null>(readLast);
  const [turns, setTurns] = useState<Turn[]>([]);
  // Proposals from a conversation loaded from history. Live ones attach to the
  // reply that made them; stored ones are listed after the thread.
  const [earlier, setEarlier] = useState<Proposal[]>([]);
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(false);
  const abort = useRef<AbortController | null>(null);
  const scroller = useRef<HTMLDivElement>(null);
  const input = useRef<HTMLTextAreaElement>(null);
  useEscapeLayer(open, onClose);

  const conversations = useQuery({
    queryKey: ["assistant", "conversations"],
    queryFn: () => api<{ conversations: { id: string; title: string; updated_at: string }[] }>("/api/assistant/conversations"),
    enabled: open,
  });

  // Load a stored conversation into turns, pairing proposals with the reply that made them.
  useEffect(() => {
    if (!open || !conversationId || busy) return;
    api<Stored>(`/api/assistant/conversations/${conversationId}`)
      .then((stored) => {
        setTurns(stored.messages.map((m) => ({ role: m.role, text: m.text, tools: m.tools.map((t, i) => ({ id: `${i}`, name: t.name, summary: t.summary, state: "done" as const })), proposals: [] })));
        setEarlier(stored.proposals);
      })
      .catch(() => {
        setConversationId(null);
        writeLast(null);
      });
  }, [open, conversationId]);

  useEffect(() => {
    if (open) setTimeout(() => input.current?.focus(), 50);
  }, [open]);

  useEffect(() => {
    scroller.current?.scrollTo({ top: scroller.current.scrollHeight });
  }, [turns]);

  const patchLast = (update: (turn: Turn) => Turn) => setTurns((all) => [...all.slice(0, -1), update(all.at(-1)!)]);

  async function send() {
    const text = draft.trim();
    if (!text || busy) return;
    setBusy(true);
    setDraft("");
    let id = conversationId;
    try {
      if (!id) {
        id = (await api<{ id: string }>("/api/assistant/conversations", { method: "POST", body: {} })).id;
        setConversationId(id);
        writeLast(id);
      }
      setTurns((all) => [...all, { role: "user", text, tools: [], proposals: [] }, { role: "assistant", text: "", tools: [], proposals: [] }]);
      abort.current = new AbortController();
      for await (const event of stream<any>(`/api/assistant/conversations/${id}/messages`, { text }, abort.current.signal)) {
        if (event.type === "text") patchLast((t) => ({ ...t, text: t.text + event.text }));
        else if (event.type === "tool")
          patchLast((t) => ({
            ...t,
            tools: t.tools.some((c) => c.id === event.id)
              ? t.tools.map((c) => (c.id === event.id ? { ...c, state: event.state } : c))
              : [...t.tools, { id: event.id, name: event.name, summary: event.summary, state: event.state }],
          }));
        else if (event.type === "proposal") patchLast((t) => ({ ...t, proposals: [...t.proposals, event.proposal] }));
        else if (event.type === "usage") patchLast((t) => ({ ...t, costUsd: (t.costUsd ?? 0) + event.costUsd }));
        else if (event.type === "error") patchLast((t) => ({ ...t, error: event.message }));
      }
    } catch (error) {
      if ((error as Error).name !== "AbortError") patchLast((t) => ({ ...t, error: (error as Error).message }));
    } finally {
      setBusy(false);
      abort.current = null;
      queryClient.invalidateQueries({ queryKey: ["assistant", "conversations"] });
    }
  }

  async function decide(proposal: Proposal, approve: boolean) {
    try {
      const { proposal: decided } = await api<{ proposal: Proposal }>(`/api/assistant/proposals/${proposal.id}`, { method: "POST", body: { approve } });
      setTurns((all) => all.map((t) => ({ ...t, proposals: t.proposals.map((p) => (p.id === decided.id ? decided : p)) })));
      setEarlier((all) => all.map((p) => (p.id === decided.id ? decided : p)));
      if (decided.status === "approved") {
        toast.push({ tone: "good", title: "Done", body: decided.summary });
        queryClient.invalidateQueries();
      } else if (decided.status === "failed") toast.push({ tone: "bad", title: "That didn't work", body: decided.error ?? undefined });
    } catch (error) {
      toast.push({ tone: "bad", title: "That didn't work", body: (error as Error).message });
    }
  }

  function startNew() {
    abort.current?.abort();
    setConversationId(null);
    writeLast(null);
    setTurns([]);
    setEarlier([]);
    input.current?.focus();
  }

  async function remove(id: string) {
    await api(`/api/assistant/conversations/${id}`, { method: "DELETE" }).catch(() => {});
    if (id === conversationId) startNew();
    queryClient.invalidateQueries({ queryKey: ["assistant", "conversations"] });
  }

  if (!open) return null;
  const aiReady = me?.ai.configured;

  return (
    <aside className="assistant" aria-label="Assistant">
      <div className="assistant-head">
        <SparkIcon size={15} />
        <strong>Assistant</strong>
        <div className="spacer" />
        <MenuButton
          label="Conversations"
          items={[
            { kind: "item", label: "New conversation", icon: <PlusIcon size={14} />, onSelect: startNew },
            ...(conversations.data?.conversations.length ? [{ kind: "divider" as const }, { kind: "section" as const, label: "Recent" }] : []),
            ...(conversations.data?.conversations ?? []).slice(0, 12).map((c) => ({
              kind: "item" as const,
              label: c.title,
              hint: ago(c.updated_at),
              onSelect: () => {
                setTurns([]);
                setConversationId(c.id);
                writeLast(c.id);
              },
            })),
            ...(conversationId ? [{ kind: "divider" as const }, { kind: "item" as const, label: "Delete this conversation", icon: <TrashIcon size={14} />, danger: true, onSelect: () => remove(conversationId) }] : []),
          ]}
        >
          <MoreIcon size={15} />
        </MenuButton>
        <button type="button" className="icon-button ghost small" onClick={onClose} aria-label="Close assistant">
          <CloseIcon size={14} />
        </button>
      </div>

      <div className="assistant-body" ref={scroller} aria-live="polite">
        {!aiReady ? (
          <div className="assistant-empty">
            <p>The assistant needs an Anthropic API key before it can answer.</p>
            {me?.user.isOwner ? <Link to="/settings" hash="ai" className="secondary-button tiny">Add the key in Settings</Link> : <p className="faint">Ask an owner to add one in Settings.</p>}
          </div>
        ) : turns.length === 0 ? (
          <div className="assistant-empty">
            <p>Ask about anything you can see in Signet. For example:</p>
            {["What's breaking out in Fitness today?", "Which picks from this week are easiest to recreate?", "Add @someone on TikTok to the Cosplay niche"].map((s) => (
              <button key={s} type="button" className="assistant-suggestion" onClick={() => setDraft(s)}>{s}</button>
            ))}
            <p className="faint small-print">Anything that changes data asks for your approval first.</p>
          </div>
        ) : (
          turns.map((turn, i) => (
            <div key={i} className={`turn ${turn.role}`}>
              {turn.tools.length ? (
                <div className="tool-chips">
                  {turn.tools.map((tool) => (
                    <span key={tool.id} className={`tool-chip ${tool.state}`}>
                      {tool.state === "start" ? <span className="spinner" style={{ width: 9, height: 9 }} /> : null}
                      {tool.summary || tool.name}
                    </span>
                  ))}
                </div>
              ) : null}
              {turn.text ? (turn.role === "assistant" ? <Markdown text={turn.text} /> : <p>{turn.text}</p>) : turn.role === "assistant" && busy && i === turns.length - 1 && !turn.tools.length ? <span className="thinking">Thinking</span> : null}
              {turn.proposals.map((p) => <ProposalCard key={p.id} proposal={p} onDecide={decide} />)}
              {turn.error ? <p className="turn-error">{turn.error}</p> : null}
              {turn.costUsd ? <small className="turn-cost">{money(turn.costUsd)}</small> : null}
            </div>
          ))
        )}
        {earlier.length ? (
          <div className="turn assistant">
            <p className="faint">Proposals in this conversation</p>
            {earlier.map((p) => <ProposalCard key={p.id} proposal={p} onDecide={decide} />)}
          </div>
        ) : null}
      </div>

      <form
        className="assistant-input"
        onSubmit={(event) => {
          event.preventDefault();
          send();
        }}
      >
        <textarea
          ref={input}
          value={draft}
          rows={2}
          placeholder={aiReady ? "Ask the assistant" : "Not available yet"}
          disabled={!aiReady}
          onChange={(event) => setDraft(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === "Enter" && !event.shiftKey) {
              event.preventDefault();
              send();
            }
          }}
        />
        {busy ? (
          <button type="button" className="icon-button" onClick={() => abort.current?.abort()} aria-label="Stop"><CloseIcon size={14} /></button>
        ) : (
          <button type="submit" className="icon-button on" disabled={!draft.trim() || !aiReady} aria-label="Send"><SendIcon size={14} /></button>
        )}
      </form>
    </aside>
  );
}

function ProposalCard({ proposal: p, onDecide }: { proposal: Proposal; onDecide(p: Proposal, approve: boolean): void }) {
  return (
    <div className={`proposal ${p.status}`}>
      <p>{p.summary}</p>
      {p.status === "pending" ? (
        <div className="row">
          <button type="button" className="button brass tiny" onClick={() => onDecide(p, true)}><CheckIcon size={12} />Approve</button>
          <button type="button" className="text-button tiny" onClick={() => onDecide(p, false)}>Reject</button>
        </div>
      ) : (
        <small>{p.status === "approved" ? "Approved and done" : p.status === "rejected" ? "Rejected" : `Failed: ${p.error}`}</small>
      )}
    </div>
  );
}
